import { useState } from 'react'
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import Bugsnag from '@bugsnag/js'
import ReactGA from 'react-ga4'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Link from '@mui/material/Link'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import IconButton from '@mui/material/IconButton'

import Loading from 'components/dumb/loading/Loading'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import AdxInput from 'components/custom/AdxInput'
import OnboardingContainer from 'components/smart/onboarding-container/OnboardingContainer'
import Maintenance from 'pages/maintenance/view/Maintenance'
import { login } from 'services/api/authentication'
import AdxButton from 'components/custom/AdxButton'
import {
  consumeReturnTo,
  consumeSessionEndReason,
  isSafeInternalReturnTo,
  resetUnauthorizedHandling,
  SESSION_ENDED_REASON,
} from 'services/session/authSession'

const LoginForm = ({ download }) => {
  const location = useLocation()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)
  const [showAuthError, setShowAuthError] = useState(false)
  const [authErrorMessage, setAuthErrorMessage] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [sessionEndReason] = useState(() => consumeSessionEndReason())

  const handleShowPassword = () => {
    setShowPassword(!showPassword)
  }

  const apiLogin = async (values) => {
    try {
      setLoading(true)
      await login(values)
      resetUnauthorizedHandling()
      setLoading(false)
      if (!download) {
        ReactGA.event('login')
        const routeState = location?.state?.from
        const stateReturnTo = routeState
          ? `${routeState.pathname || ''}${routeState.search || ''}${routeState.hash || ''}`
          : null
        const fallback = isSafeInternalReturnTo(stateReturnTo)
          ? stateReturnTo
          : '/app/homepage'
        navigate(consumeReturnTo(fallback), { replace: true })
      } else {
        await download()
      }
    } catch (e) {
      setLoading(false)
      if (e.status >= 400 && e.status < 500) {
        setShowAuthError(true)
        if (e?.data?.errors?.email) {
          setAuthErrorMessage(e.data.errors.email[0])
        } else {
          setAuthErrorMessage(e.data.message)
        }
      } else {
        Bugsnag.notify(e)
      }
    }
  }

  const validationSchema = Yup.object().shape({
    email: Yup.string().required('Please insert your email'),
    password: Yup.string().min(8).max(32).required('Please insert your password'),
  })

  const initialValues = {
    email: '',
    password: '',
  }

  const PasswordEye = () => {
    return (
      <IconButton onClick={() => handleShowPassword()} sx={{ padding: 0 }}>
        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
      </IconButton>
    )
  }

  const handleNavigateHome = () => {
    window.scrollTo(0, 0)
    navigate('/')
  }

  return (
    <OnboardingContainer>
      <Loading show={loading} />
      <Box
        height={'100%'}
        display="flex"
        justifyContent={'center'}
        alignItems={{ xs: 'flex-start', lg: 'center' }}
      >
        <Box
          width={{ xs: '100%', lg: '500px' }}
          p={2}
          display={'flex'}
          flexDirection={'column'}
          gap={2}
        >
          <Box
            component={'img'}
            src={'/adnexio-logo-text.svg'}
            alt="adnexio logo"
            sx={{ cursor: 'pointer' }}
            width={160}
            height={32}
            display={{ xs: 'block', lg: 'none' }}
          />
          <Box>
            <Typography
              variant="h1"
              mb={3}
              lineHeight={{ xs: '38px', lg: '50px' }}
              sx={{ fontWeight: 700 }}
            >
              Prepare for Launch,
              <br /> your organization awaits!
            </Typography>
            <Typography variant="body2" color={'#8A8A8A'}>
              Enter your password to continue. For first-time user, please sign-in with the password
              provided. Contact your admin if you have not receive the email credentials.
            </Typography>
          </Box>
          <Box>
            <Formik
              validationSchema={validationSchema}
              onSubmit={(values, action) => apiLogin(values, action)}
              initialValues={initialValues}
            >
              {({ handleChange, handleBlur, errors, touched }) => (
                <Form>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start', gap: 2 }}
                  >
                    <Field
                      as={AdxInput}
                      label="Email *"
                      type="email"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      name="email"
                      placeholder="example@gmail.com"
                      sx={{ width: '100%' }}
                      isErrorShown={errors?.email && touched?.email}
                      errorMessage={<AdxErrorMessage>{errors?.email}</AdxErrorMessage>}
                    />
                    <Field
                      as={AdxInput}
                      label="Password *"
                      type={showPassword ? 'text' : 'password'}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      name="password"
                      sx={{ width: '100%' }}
                      endAdornment={<PasswordEye />}
                      isErrorShown={errors?.password && touched?.password}
                      errorMessage={<AdxErrorMessage>{errors?.password}</AdxErrorMessage>}
                    />
                    <Box width="100%">
                      <Link
                        style={{ textDecoration: 'none' }}
                        href={process.env.REACT_APP_ADNEXIO_NEXT_URL + '/forgot-password'}
                        target={'_blank'}
                        rel="noreferrer"
                      >
                        <Typography variant="body1" color="alternate.main">
                          Forgot Password?
                        </Typography>
                      </Link>
                    </Box>
                    {showAuthError && (
                      <Typography variant="body2" color="error">
                        {authErrorMessage}
                      </Typography>
                    )}
                    {sessionEndReason === SESSION_ENDED_REASON && !showAuthError && (
                      <Typography variant="body2" color="error">
                        Your session has ended. Please sign in again to continue.
                      </Typography>
                    )}
                    <AdxButton type="submit" sx={{ width: 1 }}>
                      Sign In
                    </AdxButton>
                    <Typography variant="body1" textAlign="start">
                      New to adnexioHR? Onboard your organization{' '}
                      <Link
                        component={RouterLink}
                        to="/signup"
                        style={{ textDecoration: 'none' }}
                        sx={{ color: 'alternate.main', cursor: 'pointer' }}
                      >
                        here
                      </Link>
                    </Typography>
                  </Box>
                </Form>
              )}
            </Formik>
          </Box>
        </Box>
      </Box>
    </OnboardingContainer>
  )
}

const Login = (props) => {
  if (process.env.REACT_APP_MAINTENANCE_MODE === 'true') {
    return <Maintenance />
  }

  return <LoginForm {...props} />
}

export default Login
